import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Page, PageHeader, Bar, Loading, Label11, Segmented, usd, int, plural, pct } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import ObjectivesMoney from "@/pages/owner/ObjectivesMoney";
import { Plus, Users, GitCommitHorizontal, Check, TriangleAlert } from "lucide-react";

// design_handoff_crewbench/screens/Objectives.dc.html (board and dialog); the detail is ObjectiveDetail.jsx. The board
// comes in one request (GET /objectives/board): each objective with its rollup and its people's names. Money
// (ObjectivesMoney.jsx, design_handoff_team_roles/) is the other view: money per objective for a month (owner,
// 2026-10-15: Board and Money views).
const COLUMNS = [
  ["todo", "To do", "Nothing planned."],
  ["in progress", "In progress", "Nothing in progress."],
  ["review", "In review", "Nothing to review."],
  ["done", "Done", "Nothing done yet."],
];

export const dueLabel = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) : "");

function ObjectiveCard({ o }) {
  const r = o.rollup;
  const p = Math.min(100, pct(r.spent_usd, o.budget_usd));
  const people = o.assignees.map((a) => a.name || "A former member");
  const flags = o.status === "done" ? 0 : r.flagged_paths;  // a done objective's paths were checked already
  return (
    <Link to={`/founder/objectives/${o.id}`} data-testid={`objective-card-${o.title}`}
      className="bg-card rounded-[10px] p-4 flex flex-col gap-3 hover:outline hover:outline-2 hover:-outline-offset-2 hover:outline-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
      <div className="flex flex-col gap-1">
        <span className="font-display text-[15px] leading-[1.35] tracking-[0.02em] font-semibold" data-testid={`objective-link-${o.title}`}>{o.title}</span>
        <span className="text-sm text-muted-foreground line-clamp-2">{o.description || "No description yet."}</span>
      </div>
      <div className="flex flex-col gap-1.5 tabular-nums">
        <Bar pct={p} full={o.budget_usd > 0 && r.spent_usd >= o.budget_usd} />
        <div className="flex justify-between gap-2 text-sm">
          <span className="text-muted-foreground">{usd(r.spent_usd)} of {usd(o.budget_usd)}</span><span>{p}%</span>
        </div>
      </div>
      {r.sessions > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground tabular-nums">
          <span className="flex items-center gap-1"><GitCommitHorizontal className="w-3.5 h-3.5" />{int(r.commits)}</span>
          <span><span className="text-gr-success">+{int(r.lines_added)}</span> <span className="text-gr-danger">−{int(r.lines_removed)}</span></span>
          <span>{plural(r.sessions, "session")}{r.active > 0 && ` · ${int(r.active)} active`}</span>
        </div>
      )}
      {flags > 0 && (
        <div className="flex items-center gap-1.5 text-sm text-gr-warning tabular-nums">
          <TriangleAlert className="w-3.5 h-3.5 shrink-0" /><span>{plural(flags, "path")} to check before merging</span>
        </div>
      )}
      <div className="flex justify-between items-center gap-2 pt-2.5 border-t text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5 min-w-0"><Users className="w-3.5 h-3.5 shrink-0" /><span>{people.length ? people.join(", ") : "Nobody assigned"}</span></span>
        <span className="whitespace-nowrap">{o.due_date ? `Due ${dueLabel(o.due_date)}` : "No due date"}</span>
      </div>
    </Link>
  );
}

const blank = (projectId) => ({ project_id: projectId || "", title: "", description: "", success_criteria: "", assignees: [],
                                budget_usd: "50", due_date: "" });

export function NewObjectiveDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data, enabled: open });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data, enabled: open });
  const firstProject = projects?.projects?.[0]?.id;
  const [f, setF] = useState(blank());
  const titleRef = useRef(null);
  useEffect(() => { if (open) setF(blank(firstProject)); }, [open, firstProject]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  // team members only: independent professionals join an objective with hiring terms (backend/assignment_routes.py)
  const coders = (team?.members || []).filter((m) => m.role === "coder" && m.engagement === "employee");
  const create = useMutation({
    mutationFn: async () => (await api.post("/objectives", {
      ...f, project_id: f.project_id || null, title: f.title.trim(), description: f.description.trim(),
      success_criteria: f.success_criteria.trim(), budget_usd: Math.max(0, Number(f.budget_usd) || 0), due_date: f.due_date || null,
    })).data,
    onSuccess: () => {
      ["objectives", "overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onOpenChange(false);
      toast.success("Objective created. It's in To do.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const ready = f.title.trim().length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px]" data-testid="objective-dialog"
        onOpenAutoFocus={(e) => { e.preventDefault(); titleRef.current?.focus(); }}>
        <div className="flex flex-col gap-1">
          <DialogTitle>New objective</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
            Every session for it gets the title, the description and "Done when" as its goal.
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (ready && !create.isPending) create.mutate(); }}>
          <div className="flex flex-col gap-1.5">
            <Label>Project</Label>
            <Select value={f.project_id} onValueChange={(v) => setF((x) => ({ ...x, project_id: v }))}>
              <SelectTrigger data-testid="objective-project"><SelectValue placeholder="No project" /></SelectTrigger>
              <SelectContent>{projects?.projects?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-title">Title</Label>
            <Input id="obj-title" ref={titleRef} data-testid="objective-title" value={f.title} onChange={set("title")} placeholder="e.g. Refund flow for cancelled bookings" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-desc">Description</Label>
            <Textarea id="obj-desc" rows={3} data-testid="objective-desc" value={f.description} onChange={set("description")} placeholder="What should it do, and for whom?" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="obj-done">Done when</Label>
            <Textarea id="obj-done" rows={2} className="min-h-0" data-testid="objective-done" value={f.success_criteria} onChange={set("success_criteria")}
              placeholder="e.g. A cancelled booking is refunded in Stripe within a minute." />
            <span className="text-sm text-muted-foreground">You'll use this to accept the work.</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold">Assigned to</span>
            <div className="flex flex-wrap gap-2">
              {coders.length === 0 && <span className="text-sm text-muted-foreground">No team members to assign yet.</span>}
              {coders.map((m) => {
                const on = f.assignees.includes(m.id);
                return (
                  <button key={m.id} type="button" aria-pressed={on}
                    onClick={() => setF((x) => ({ ...x, assignees: on ? x.assignees.filter((a) => a !== m.id) : [...x.assignees, m.id] }))}
                    className={cn("inline-flex items-center gap-1.5 h-8 px-3 rounded-md font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                      on ? "bg-gr-selected text-gr-on-selected" : "border border-gr-control-border hover:bg-gr-hover")}>
                    {on && <Check className="w-4 h-4" />}{m.name || m.email}
                  </button>
                );
              })}
            </div>
            <span className="text-sm text-muted-foreground">Team members only. You hire independent professionals for an objective from their Team card, with a fee and their agreement.</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex-[1_1_180px] flex flex-col gap-1.5">
              <Label htmlFor="obj-budget">Budget (USD)</Label>
              <Input id="obj-budget" type="number" min="0" step="1" className="tabular-nums" data-testid="objective-budget"
                value={f.budget_usd} onChange={set("budget_usd")} />
              <span className="text-sm text-muted-foreground">Sessions for it stop when it's used up.</span>
            </div>
            <div className="flex-[1_1_180px] flex flex-col gap-1.5">
              <Label htmlFor="obj-due">Due date <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="obj-due" type="date" className="dark:[color-scheme:dark]" value={f.due_date} onChange={set("due_date")} />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <span className="text-sm text-muted-foreground">{ready ? "" : "Add a title to create it."}</span>
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" aria-disabled={!ready} disabled={create.isPending} data-testid="objective-create-btn">Create objective</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Objectives() {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  const view = params.get("view") === "money" ? "money" : "board";
  // under "objectives", so whatever refreshes the objectives refreshes the board too
  const { data, isLoading } = useQuery({ queryKey: ["objectives", "board"], refetchInterval: 15000, enabled: view === "board",
                                         queryFn: async () => (await api.get("/objectives/board")).data });
  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && params.get("new")) { const p = new URLSearchParams(params); p.delete("new"); setParams(p, { replace: true }); }
  };
  const action = <Button onClick={() => setOpen(true)} data-testid="objective-open-btn"><Plus />New objective</Button>;
  const dialog = <NewObjectiveDialog open={open} onOpenChange={onOpenChange} />;
  // the month and project filters belong to Money: switching views drops them
  const viewSwitch = <Segmented label="View" value={view} options={[["board", "Board"], ["money", "Money"]]} testid="objectives-view"
    onChange={(v) => setParams(v === "money" ? { view: "money" } : {}, { replace: true })} />;
  if (view === "money") return <ObjectivesMoney viewSwitch={viewSwitch} action={action} dialog={dialog} />;
  if (isLoading || !data) return <Page><PageHeader eyebrow="Objectives" title="Objectives" actions={action} />{viewSwitch}<Loading />{dialog}</Page>;

  const objs = data.objectives;
  const projects = [...new Set(objs.map((o) => o.project_name).filter(Boolean))];
  const spent = objs.reduce((a, o) => a + o.rollup.spent_usd, 0);
  const budget = objs.reduce((a, o) => a + (o.budget_usd || 0), 0);
  const review = objs.filter((o) => o.status === "review").length;
  return (
    <Page data-testid="owner-objectives">
      <PageHeader eyebrow={`${projects.length === 1 ? projects[0] : "All projects"} · ${plural(objs.length, "objective")}`}
        title={`${usd(spent)} of ${usd(budget)} spent across ${plural(objs.length, "objective")}.`}
        status={review === 0 ? "Nothing is waiting for your review." : review === 1 ? "1 is waiting for your review." : `${review} are waiting for your review.`}
        actions={action} />
      <div>{viewSwitch}</div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 items-start">
        {COLUMNS.map(([status, label, empty]) => {
          const items = objs.filter((o) => o.status === status);
          return (
            <section key={status} className="flex flex-col gap-3 min-w-0" data-testid={`objective-column-${status}`}>
              <div className="flex items-center justify-between px-1">
                <Label11>{label}</Label11>
                <span className="px-1.5 rounded bg-gr-surface-2 text-xs leading-[18px] tabular-nums">{items.length}</span>
              </div>
              {items.map((o) => <ObjectiveCard key={o.id} o={o} />)}
              {items.length === 0 && <div className="border border-dashed rounded-[10px] px-4 py-5 text-sm text-muted-foreground text-center">{empty}</div>}
            </section>
          );
        })}
      </div>
      {dialog}
    </Page>
  );
}
